import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useClients } from "../../hooks/useClients";
import ClientTable from "../../components/ClientTable";
import { usePageContext } from "../../context/PageContext";
import { fetchData } from "../../api/client";
import { resolveApiErrorNotice } from "../../components/ApiErrorAlertBody";
import { getSelectedCompanyIds } from "../../utils/workspaceStorage";
import { useTranslation } from "react-i18next";
import MergeClientsDialog from "../../components/clients/MergeClientsDialog";
import ClientAdvancedFiltersDialog from "../../components/clients/ClientAdvancedFiltersDialog";
import ClientHygieneDialog from "../../components/clients/ClientHygieneDialog";
import OmnichannelCampaignCreateDialog from "../Campaigns/components/OmnichannelCampaignCreateDialog";
import { useClientFilters } from "../../hooks/useClientFilters";
import { usePortalClienteChannelConfigured } from "../../hooks/usePortalClienteChannelConfigured";
import { useAuthContext } from "../../context/AuthContext";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import {
  buildPortalBulkInviteFeedback,
  mapPortalBulkInviteApiError,
  summarizePortalInviteSelection,
} from "../../utils/clientPortalBulkInviteFeedback";

const Clients = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notice, setNotice] = useState(null); // { message: string, severity: "success"|"info"|"warning"|"error" }
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hygieneOpen, setHygieneOpen] = useState(false);
  const [mergeClientsOverride, setMergeClientsOverride] = useState(null);
  const [portalInviteConfirmOpen, setPortalInviteConfirmOpen] = useState(false);
  const [portalInviteTargetIds, setPortalInviteTargetIds] = useState([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [campaignClientIds, setCampaignClientIds] = useState([]);

  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const permissionScopes = scopeSetFromPermissions(permissionsData, userData);
  const canCreateCampaign = can(permissionScopes, "campaigns", "create");
  const canManageClients = can(permissionScopes, "clients", "manage");

  const { configured: portalChannelConfigured, isLoading: portalChannelLoading } =
    usePortalClienteChannelConfigured();

  const {
    filters,
    appliedFilters,
    patchFilters,
    applyFilters,
    clearFilters,
    resetDraftToApplied,
    draftActiveCount,
    queryString: filtersQuery,
    activeCount: activeFilterCount,
    hasActiveFilters,
  } = useClientFilters();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    deleteMutation,
    isLoading,
  } = useClients(currentPage, pageSize, search, null, filtersQuery);

  useEffect(() => {
    if (error) {
      setNotice({
        message: "Erro ao carregar clientes. Por favor, tente novamente.",
        severity: "error",
      });
    }
  }, [error]);

  const handleEditClient = (clientOrId) => {
    const id = typeof clientOrId === "object" ? clientOrId?.id : clientOrId;
    if (!id) return;
    navigate(`/clients/${id}/edit`);
  };

  const handleAddClient = () => {
    navigate("/clients/new");
  };

  const handleDeleteClient = (id) => {
    setConfirmDelete(id);
    setNotice(null);
  };

  const confirmDeleteClient = () => {
    if (!confirmDelete) return;

    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: () => {
        setNotice({
          message: "Erro ao excluir o cliente. Por favor, tente novamente.",
          severity: "error",
        });
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  // <<< NOVO: exclusão em massa >>>
  const handleDeleteSelected = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;

    setIsProcessing(true);
    setNotice(null);

    try {
      // Caso não exista um endpoint de bulk no backend, removemos 1 a 1.
      // Se você tiver /clients/bulk-delete, troque por uma única chamada POST/DELETE.
      const results = await Promise.allSettled(
        ids.map((id) => fetchData(`/clients/${id}`, "DELETE"))
      );

      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        setNotice({
          message: `Nem todos os registros foram excluídos: ${failures} falharam de ${ids.length}.`,
          severity: "warning",
        });
      }
      await refetch();
    } catch (e) {
      setNotice({
        message: e?.message || "Erro ao excluir os clientes selecionados.",
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };
  // <<< FIM NOVO >>>

  const handlePortalInviteSelected = (selectedRows, selectedIds) => {
    const idsFromRows = (selectedRows || [])
      .map((row) => {
        if (row == null) return null;
        if (typeof row === "object") return row.id ?? row?._raw?.id ?? null;
        return row;
      })
      .filter((id) => id != null);

    const ids = (selectedIds?.length ? selectedIds : idsFromRows)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (!ids.length) {
      setNotice({
        severity: "warning",
        title: t("clientTable.portalInviteErrorTitle"),
        lines: [t("clientTable.portalInviteNeedSelection")],
      });
      return;
    }

    if (!portalChannelConfigured && !portalChannelLoading) {
      setNotice({
        severity: "warning",
        title: t("clientTable.portalInviteErrorTitle"),
        lines: [t("clientTable.portalInviteChannelRequired")],
      });
      return;
    }

    setPortalInviteTargetIds(ids);
    setPortalInviteConfirmOpen(true);
  };

  const handleCreateCampaignSelected = (selectedRows, selectedIds) => {
    const idsFromRows = (selectedRows || [])
      .map((row) => row?.id ?? row?._raw?.id ?? null)
      .filter((id) => id != null);
    const ids = [...new Set((selectedIds?.length ? selectedIds : idsFromRows)
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0))];

    if (!ids.length) {
      setNotice({
        severity: "warning",
        message: "Selecione ao menos um cliente para criar o disparo.",
      });
      return;
    }
    setCampaignClientIds(ids);
    setCampaignDialogOpen(true);
  };

  const portalInvitePreview = summarizePortalInviteSelection(
    (data?.clients || []).filter((c) =>
      portalInviteTargetIds.some((id) => String(id) === String(c.id))
    )
  );

  const confirmPortalInviteSelected = async () => {
    const ids = portalInviteTargetIds;
    if (!Array.isArray(ids) || ids.length === 0) return;

    setIsProcessing(true);
    setPortalInviteConfirmOpen(false);

    try {
      const res = await fetchData("/clients/portal/invite-bulk", "POST", { ids });
      const payload = res?.data || res;
      const feedback = buildPortalBulkInviteFeedback(payload, t);
      setNotice(feedback);
      await refetch();
    } catch (e) {
      setNotice(mapPortalBulkInviteApiError(e, t));
    } finally {
      setIsProcessing(false);
      setPortalInviteTargetIds([]);
    }
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleSearch = (searchTerm) => {
    setSearch(typeof searchTerm === "string" ? searchTerm : "");
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    applyFilters();
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    clearFilters();
    setCurrentPage(1);
  };

  const filterTotal = data?.pagination?.total || 0;

  /**
   * Iniciar Conversa (nova solicitação de RELACIONAMENTO) para um cliente
   */
  const handleStartConversation = async (client) => {
    if (!client?.id) return;
    try {
      setIsProcessing(true);
      setNotice(null);

      const selected = getSelectedCompanyIds();
      const projectId = selected.length ? Number(selected[0]) : null;

      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "clients-page",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;

      if (!conv?.id) {
        throw new Error("A API não retornou o ID da solicitação criada.");
      }

      const width = window.screen.width;
      const height = window.screen.height;
      window.open(
        `/popup-call/${conv.id}`,
        "SolicitaçãoPopup",
        `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
      );
    } catch (e) {
      const notice = resolveApiErrorNotice(
        e,
        "Não foi possível iniciar a conversa."
      );
      setNotice({
        severity: "error",
        title: notice.message,
        lines: notice.action ? [notice.action] : undefined,
        message: notice.action ? undefined : notice.message,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box className="tour-clients-root" sx={{ paddingTop: 1 }}>
      {notice ? (
        <Box sx={{ mb: 2 }}>
          <Alert severity={notice.severity || "info"} onClose={() => setNotice(null)}>
            {notice.title ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {notice.title}
                </Typography>
                {(notice.lines || []).map((line, index) => (
                  <Typography
                    key={`${index}-${line}`}
                    variant="body2"
                    sx={{ mt: index === 0 ? 0 : 0.75 }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            ) : (
              notice.message
            )}
          </Alert>
        </Box>
      ) : null}

      <Box
        className="tour-clients-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("clientsPage.title")}
        </Typography>
        {canManageClients ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            onClick={handleAddClient}
            disabled={isProcessing}
          >
            {t("clientsPage.addClient")}
          </Button>
        ) : null}
      </Box>

      <Box className="tour-clients-table">
        <ClientTable
          clients={data?.clients || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditClient={handleEditClient}
          onDeleteClient={handleDeleteClient}
          onStartConversation={handleStartConversation}
          onDeleteSelected={handleDeleteSelected}
          onSelectionChange={(ids) => setSelectedClientIds(Array.isArray(ids) ? ids : [])}
          onMergeSelected={() => setMergeDialogOpen(true)}
          onOpenFilters={() => setFiltersOpen(true)}
          activeFilterCount={activeFilterCount}
          hasActiveFilters={hasActiveFilters}
          onReviewSelected={() => setHygieneOpen(true)}
          onReviewFilter={() => setHygieneOpen(true)}
          onPortalInviteSelected={handlePortalInviteSelected}
          portalInviteDisabled={portalChannelLoading || !portalChannelConfigured}
          portalInviteDisabledReason={t("clientTable.portalInviteChannelRequired")}
          onCreateCampaignSelected={handleCreateCampaignSelected}
          campaignCreateEnabled={canCreateCampaign}
          canManageClients={canManageClients}
        />
      </Box>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? "Processando exclusão..."
              : "Tem certeza de que deseja excluir este cliente?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteClient}>
                Excluir
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={portalInviteConfirmOpen}
        onClose={() => !isProcessing && setPortalInviteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("clientTable.portalInviteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              {t("clientTable.portalInviteConfirmMessage", {
                count: portalInviteTargetIds.length,
              })}
            </Typography>
            {portalInvitePreview.withEmailCount > 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {portalInvitePreview.withEmailCount === 1
                  ? t("clientTable.portalInviteConfirmReadyOne")
                  : t("clientTable.portalInviteConfirmReadyMany", {
                      count: portalInvitePreview.withEmailCount,
                    })}
              </Alert>
            ) : null}
            {portalInvitePreview.withoutEmailCount > 0 ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {portalInvitePreview.withoutEmailCount === 1
                    ? t("clientTable.portalInviteConfirmMissingEmailOne")
                    : t("clientTable.portalInviteConfirmMissingEmailMany", {
                        count: portalInvitePreview.withoutEmailCount,
                      })}
                </Typography>
                <Typography variant="body2">
                  {t("clientTable.portalInviteConfirmMissingEmailHint")}
                </Typography>
                {portalInvitePreview.withoutEmailNames.length ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {portalInvitePreview.withoutEmailNames.join(" · ")}
                    {portalInvitePreview.withoutEmailCount >
                    portalInvitePreview.withoutEmailNames.length
                      ? ` · ${t("clientTable.portalInviteConfirmAndOthers")}`
                      : ""}
                  </Typography>
                ) : null}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortalInviteConfirmOpen(false)} disabled={isProcessing}>
            {t("shared.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={confirmPortalInviteSelected}
            disabled={isProcessing}
          >
            {isProcessing
              ? t("clientTable.portalInviteSending")
              : t("clientTable.portalInviteConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <MergeClientsDialog
        open={mergeDialogOpen}
        clients={
          mergeClientsOverride ||
          (data?.clients || []).filter((c) =>
            (selectedClientIds || []).some((id) => String(id) === String(c.id))
          )
        }
        onClose={() => {
          setMergeDialogOpen(false);
          setMergeClientsOverride(null);
        }}
        onMerged={async ({ primaryId }) => {
          setMergeDialogOpen(false);
          setMergeClientsOverride(null);
          await refetch?.();
          setNotice({
            message: t("clientHygiene.mergeSuccessNotice", { id: primaryId }),
            severity: "success",
          });
        }}
      />

      <ClientAdvancedFiltersDialog
        open={filtersOpen}
        filters={filters}
        draftActiveCount={draftActiveCount}
        onPatch={patchFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onClose={() => {
          resetDraftToApplied();
          setFiltersOpen(false);
        }}
      />

      <ClientHygieneDialog
        open={hygieneOpen}
        onClose={() => setHygieneOpen(false)}
        selectedIds={selectedClientIds}
        appliedFilters={appliedFilters}
        hasActiveFilters={hasActiveFilters}
        filterTotal={filterTotal}
        onApplied={async () => {
          await refetch?.();
          setNotice({
            message: t("clientHygiene.applySuccessNotice"),
            severity: "success",
          });
        }}
        onMergeDuplicates={(primaryId, secondaries, options) => {
          const members =
            options?.members ||
            [{ id: primaryId, nome: `#${primaryId}` }, ...(secondaries || [])];
          setMergeClientsOverride(
            members.map((m) => ({
              id: m.id,
              nome: m.nome || `#${m.id}`,
            }))
          );
          setMergeDialogOpen(true);
        }}
      />

      {canCreateCampaign ? (
        <OmnichannelCampaignCreateDialog
          open={campaignDialogOpen}
          initialClientIds={campaignClientIds}
          onClose={() => {
            setCampaignDialogOpen(false);
            setCampaignClientIds([]);
          }}
          onCreated={(campaignId) => {
            setCampaignDialogOpen(false);
            setCampaignClientIds([]);
            if (campaignId) navigate(`/campaigns/whatsapp/${campaignId}`);
          }}
        />
      ) : null}
    </Box>
  );
};

export default Clients;
